import { OpenPanel } from "@openpanel/web";
import type {
  AnalyticsEvent,
  BaseAnalyticsServiceObject,
  CertaAnalyticsTrackProperties
} from "../analytics.type";

let openPanelInstance: OpenPanel | null = null;
const defaultOpenPanelHost = "https://analytics.certa.dev/api";
// New solution
// hostname/api/track
//  note the regular headers which are being sent for initialize and  headers track and all
const token = import.meta.env.REACT_APP_OPENPANEL_ANALYTICS_PACKAGE_TOKEN;
const logAnalyticsInitFailure = () =>
  console.log("Unable to initialize Openpanel Analytics Service.");

const getAnalyticsInstance = (): OpenPanel | null => {
  if (token) {
    const apiHost =
      import.meta.env.REACT_APP_OPENPANEL_HOST || defaultOpenPanelHost;

    const op = new OpenPanel({
      apiUrl: apiHost,
      clientId: token,
      trackScreenViews: true,
      trackOutgoingLinks: true,
      trackAttributes: true,
      trackHashChanges: true
    });
    return op;
  } else {
    logAnalyticsInitFailure();
    return null;
  }
};

const initializeAnalytics = () => {
  try {
    openPanelInstance = getAnalyticsInstance();
    if (openPanelInstance) {
      openPanelInstance.init();
      console.log("Analytics Service Initialized");
    } else {
      logAnalyticsInitFailure();
    }
  } catch (e) {
    logAnalyticsInitFailure();
    console.error(e);
  }
};

export const openPanelAnalytics = (): BaseAnalyticsServiceObject => {
  initializeAnalytics();

  return {
    /** Track an event */
    track: (
      name: keyof AnalyticsEvent[keyof AnalyticsEvent],
      props?: CertaAnalyticsTrackProperties
    ) => {
      if (!openPanelInstance) {
        logAnalyticsInitFailure();
        return;
      }

      if (token) {
        openPanelInstance.track(name, props);
      } else {
        logAnalyticsInitFailure();
      }
    },

    /** Identify a user */
    identify: (distinctId: string) => {
      if (!openPanelInstance) {
        logAnalyticsInitFailure();
        return;
      }
      if (token) {
        openPanelInstance.identify({ profileId: distinctId });
      } else {
        logAnalyticsInitFailure();
      }
    },

    /** Sets global properties */
    register: (props: Record<string, unknown>) => {
      if (!openPanelInstance) {
        logAnalyticsInitFailure();
        return;
      }
      if (token) {
        openPanelInstance.setGlobalProperties(props);
      } else {
        logAnalyticsInitFailure();
      }
    },
    reset: () => {
      if (!openPanelInstance) {
        logAnalyticsInitFailure();
        return;
      }
      if (token) {
        openPanelInstance.clear();
      } else {
        logAnalyticsInitFailure();
      }
    }
  } as const;
};
